import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Siren, Search, Check, Archive, RotateCcw, Eye } from 'lucide-react';
import { Spinner, TableSkeleton } from '@components/medicus-ui/Loaders';
import { mensajeDeError } from '@lib/mensaje-error';
import { useAuthStore } from '@lib/store/auth.store';
import { puedeVer } from '@lib/permissions/catalog';
import {
  getAlertasBajas,
  resolverAlertaBaja,
  type AlertaBaja,
  type AlertaBajaEstado,
  type AlertaBajaTipo,
} from '@lib/services/bajas.service';

const TIPO_CHIP: Record<AlertaBajaTipo, { label: string; color: string; bg: string }> = {
  // Histórico: de cuando MS2 no se procesaba. Ya no se generan, pero quedan
  // alertas guardadas con este tipo.
  ms2: { label: 'Plan MS2', color: '#6d28d9', bg: '#ede9fe' },
  plan_desconocido: { label: 'Plan sin determinar', color: '#6d28d9', bg: '#ede9fe' },
  titular_con_grupo: { label: 'Titular con familia', color: '#6d28d9', bg: '#ede9fe' },
  fallecimiento_con_grupo: { label: 'Fallecido con grupo', color: '#6d28d9', bg: '#ede9fe' },
  fallecimiento_manual: { label: 'Fallecimiento manual', color: '#6d28d9', bg: '#ede9fe' },
  rechazo_binary: { label: 'Rechazo de Binary', color: '#b91c1c', bg: '#f9e2e5' },
  // Datos incompletos del archivo: ámbar, porque se resuelven corrigiendo y
  // volviendo a subir, no requieren una gestión manual como las violetas.
  sin_motivo: { label: 'Sin motivo', color: '#b45309', bg: '#f8ecd2' },
  motivo_invalido: { label: 'Motivo no habilitado', color: '#b45309', bg: '#f8ecd2' },
  sin_fecha_fallecimiento: {
    label: 'Falta fecha de fallecimiento',
    color: '#b45309',
    bg: '#f8ecd2',
  },
  vigencia_antes_fallecimiento: {
    label: 'Vigencia anterior al fallecimiento',
    color: '#b45309',
    bg: '#f8ecd2',
  },
};

const ESTADO_CHIP: Record<AlertaBajaEstado, { label: string; color: string; bg: string }> = {
  abierta: { label: 'Abierta', color: '#b45309', bg: '#f8ecd2' },
  resuelta: { label: 'Resuelta', color: '#166534', bg: '#e2f3e8' },
  archivada: { label: 'Archivada', color: '#6b7280', bg: '#f1f3f5' },
};

const TIPOS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todos los tipos' },
  { value: 'ms2', label: 'Plan MS2' },
  { value: 'plan_desconocido', label: 'Plan sin determinar' },
  { value: 'titular_con_grupo', label: 'Titular con familia' },
  { value: 'fallecimiento_con_grupo', label: 'Fallecido con grupo' },
  { value: 'fallecimiento_manual', label: 'Fallecimiento manual' },
  { value: 'sin_motivo', label: 'Sin motivo' },
  { value: 'motivo_invalido', label: 'Motivo no habilitado' },
  { value: 'sin_fecha_fallecimiento', label: 'Falta fecha de fallecimiento' },
  { value: 'vigencia_antes_fallecimiento', label: 'Vigencia anterior al fallecimiento' },
  { value: 'rechazo_binary', label: 'Rechazo de Binary' },
];

const chipStyle = (c: { color: string; bg: string }): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 9px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  color: c.color,
  background: c.bg,
  whiteSpace: 'nowrap',
});

function fmtFecha(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

/**
 * Sección "Alertas Bajas" (debajo de Bajas). Casos que NO se procesan
 * automáticamente y necesitan intervención manual: fallecimiento sobre
 * titular con grupo (rearmado), fallecimiento retroactivo/temprano; y los
 * rechazos de Binary al confirmar, con el error descriptivo.
 *
 * DOS permisos, no uno:
 *  - `view:alertas-bajas` deja ENTRAR y mirar en qué estado quedó cada caso. La
 *    organización (USUARIO_EMPRESA) lo tiene: ve las alertas de su gente.
 *  - `view:alertas-bajas-gestionar` deja CAMBIAR el estado. No es cosmético:
 *    marcar "gestionada" verifica contra Binary y espeja la baja en Mongo. Se
 *    otorga usuario por usuario desde Usuarios.
 *
 * Esconder los botones es solo UX: el backend rechaza el POST igual (403).
 */
export function AlertasBajasPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const user = useAuthStore((s) => s.user);
  const puedeGestionar = Boolean(user && puedeVer(user, 'view:alertas-bajas-gestionar'));
  const [estado, setEstado] = useState<string>('abierta');
  const [tipo, setTipo] = useState<string>('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const query = useQuery({
    queryKey: ['alertas-bajas', qDebounced, estado, tipo, page],
    queryFn: () => getAlertasBajas({ page, pageSize, q: qDebounced, estado, tipo }),
  });

  // El server verifica contra Binary al marcar gestionada; se muestra qué
  // encontró, porque el resultado cambia lo que el operador tiene que hacer
  // después (si sigue activa, la baja NO está hecha).
  const [avisoVerif, setAvisoVerif] = useState<{ ok: boolean; texto: string } | null>(null);
  const mut = useMutation({
    mutationFn: ({ id, nuevo }: { id: string; nuevo: AlertaBajaEstado }) =>
      resolverAlertaBaja(id, nuevo),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['alertas-bajas'] });
      setAvisoVerif(
        r.verificacion ? { ok: !r.verificacion.activo, texto: r.verificacion.mensaje } : null,
      );
    },
    // Un 403 (sin permiso de gestionar) o un fallo al verificar en Binary
    // dejaban el click sin efecto y sin explicación, en la pantalla cuyo
    // único propósito es no perder bajas manuales.
    onError: (e) =>
      setAvisoVerif({
        ok: false,
        texto: mensajeDeError(e, 'No se pudo marcar la alerta como gestionada.'),
      }),
  });

  function buscar(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setQDebounced(q.trim());
  }

  const data = query.data;
  const totalPaginas = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Siren size={22} style={{ color: '#6d28d9' }} /> Alertas Bajas
        </h1>
        <p className="text-element-mid">
          Bajas que no se procesan automáticamente y requieren revisión manual: fallecimiento sobre
          titular con grupo (rearmado), fallecimiento retroactivo, plan sin determinar, y los
          rechazos de Binary.
        </p>
      </header>

      {!puedeGestionar && (
        <p
          className="status status-info"
          style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <Eye size={15} />
          Estás viendo el estado de las alertas. Para gestionarlas —marcar como gestionada,
          archivar o reabrir— un administrador tiene que habilitarte el permiso desde Usuarios.
        </p>
      )}

      {avisoVerif && (
        <p
          className={avisoVerif.ok ? 'status status-ok' : 'status status-error'}
          style={{ marginBottom: 12 }}
        >
          {avisoVerif.texto}{' '}
          <button
            type="button"
            className="btn btn-ghost"
            style={{ padding: '0 6px', fontSize: 12 }}
            onClick={() => setAvisoVerif(null)}
          >
            cerrar
          </button>
        </p>
      )}

      <form
        onSubmit={buscar}
        style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}
      >
        <div style={{ position: 'relative', flex: '1 1 260px' }}>
          <Search
            size={16}
            style={{ position: 'absolute', left: 10, top: 11, color: '#9ca3af', pointerEvents: 'none' }}
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="DNI, CUIL, nombre, legajo o N° solicitud"
            style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: 8, border: '1px solid #d1d5db' }}
          />
        </div>
        <select
          value={estado}
          onChange={(e) => {
            setEstado(e.target.value);
            setPage(1);
          }}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
        >
          <option value="abierta">Abiertas</option>
          <option value="resuelta">Resueltas</option>
          <option value="archivada">Archivadas</option>
          <option value="">Todas (menos archivadas)</option>
        </select>
        <select
          value={tipo}
          onChange={(e) => {
            setTipo(e.target.value);
            setPage(1);
          }}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
        >
          {TIPOS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <button type="submit" className="btn btn-primary">
          Buscar
        </button>
      </form>

      {query.isLoading ? (
        <TableSkeleton />
      ) : query.isError ? (
        <p className="status status-error">No se pudieron cargar las alertas.</p>
      ) : !data || data.items.length === 0 ? (
        <p className="text-element-mid">No hay alertas para los filtros elegidos.</p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={thStyle}>Persona</th>
                  <th style={thStyle}>Tipo</th>
                  <th style={thStyle}>Detalle</th>
                  <th style={thStyle}>Vigencia</th>
                  <th style={thStyle}>N° sol.</th>
                  <th style={thStyle}>Estado</th>
                  <th style={thStyle}>Origen</th>
                  {puedeGestionar && <th style={thStyle}></th>}
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <Fila
                    key={a.id}
                    a={a}
                    onCambiar={(nuevo) => mut.mutate({ id: a.id, nuevo })}
                    // Solo la fila que se está gestionando: antes se
                    // deshabilitaban todas y no se veía cuál estaba en curso.
                    guardando={mut.isPending && mut.variables?.id === a.id}
                    puedeGestionar={puedeGestionar}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-end', marginTop: 14 }}>
            <span className="text-element-mid">
              {data.total} {data.total === 1 ? 'alerta' : 'alertas'} · página {data.page} de{' '}
              {totalPaginas}
            </span>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Anterior
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={page >= totalPaginas}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente →
            </button>
          </div>
        </>
      )}
    </section>
  );
}

const thStyle: React.CSSProperties = { padding: '8px 10px', fontWeight: 600 };
const tdStyle: React.CSSProperties = { padding: '10px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'top' };

function Fila({
  a,
  onCambiar,
  guardando,
  puedeGestionar,
}: {
  a: AlertaBaja;
  onCambiar: (nuevo: AlertaBajaEstado) => void;
  guardando: boolean;
  /** Sin este permiso la fila es de lectura: no se dibuja ni la columna. */
  puedeGestionar: boolean;
}) {
  return (
    <tr>
      <td style={tdStyle}>
        <div style={{ fontWeight: 600 }}>{a.nombre || '—'}</div>
        <div className="text-element-mid" style={{ fontSize: 12 }}>
          DNI {a.documento || '—'}
          {a.legajo ? ` · Legajo ${a.legajo}` : ''}
        </div>
      </td>
      <td style={tdStyle}>
        <span style={chipStyle(TIPO_CHIP[a.tipo])}>{TIPO_CHIP[a.tipo].label}</span>
      </td>
      <td style={{ ...tdStyle, maxWidth: 320 }}>{a.mensaje}</td>
      <td style={tdStyle}>
        {a.fechaVigencia || '—'}
        {a.fechaFallecimiento ? (
          <div className="text-element-mid" style={{ fontSize: 12 }}>
            † {a.fechaFallecimiento}
          </div>
        ) : null}
      </td>
      <td style={tdStyle}>{a.solafiId || '—'}</td>
      <td style={tdStyle}>
        <span style={chipStyle(ESTADO_CHIP[a.estado])}>{ESTADO_CHIP[a.estado].label}</span>
      </td>
      <td style={tdStyle}>
        <div className="text-element-mid" style={{ fontSize: 12 }}>
          {a.creadoPor || '—'}
          <br />
          {fmtFecha(a.creadoAt)}
        </div>
      </td>
      {puedeGestionar && (
        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
          {a.estado === 'abierta' ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                className="btn btn-ghost"
                title="Marcar como gestionada: se verifica el estado real en Binary"
                disabled={guardando}
                onClick={() => onCambiar('resuelta')}
                style={{ padding: '4px 8px' }}
              >
                {guardando ? <Spinner size={13} /> : <Check size={15} />}{' '}
                {guardando ? 'Verificando en Binary…' : 'Marcar como gestionada'}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                title="Archivar"
                disabled={guardando}
                onClick={() => onCambiar('archivada')}
                style={{ padding: '4px 8px', color: '#6b7280' }}
              >
                <Archive size={15} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-ghost"
              title="Reabrir"
              disabled={guardando}
              onClick={() => onCambiar('abierta')}
              style={{ padding: '4px 8px' }}
            >
              <RotateCcw size={15} /> Reabrir
            </button>
          )}
        </td>
      )}
    </tr>
  );
}
